import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  UserPlus,
  Search,
  Filter,
  Download,
  ChevronLeft,
  ChevronRight,
  Mail,
  Phone,
  Calendar,
  Shield,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  Loader2,
  Eye,
  Ban,
  UserCheck,
  Package,
  DollarSign,
  RefreshCw
} from 'lucide-react';
const BASE_URL=process.env.REACT_APP_API_BASE_URL;
// const BASE_URL=process.env.REACT_APP_API_URL||"https://styles-questions-ver-mat.trycloudflare.com";

const UserManagement = () => {
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'guests'
  const [users, setUsers] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Filter states for Users
  const [userFilters, setUserFilters] = useState({
    search: '',
    searchInput: '',
    role: '',
    isActive: '',
    suspended: '',
    isVerified: '',
    authProvider: '',
    gender: '',
    profession: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // Filter states for Guests
  const [guestFilters, setGuestFilters] = useState({
    search: '',
    searchInput: '',
    isBlacklisted: '',
    hasOrders: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // Fetch Users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(userFilters.search && { search: userFilters.search }),
        ...(userFilters.role && { role: userFilters.role }),
        ...(userFilters.isActive && { isActive: userFilters.isActive }),
        ...(userFilters.suspended && { suspended: userFilters.suspended }),
        ...(userFilters.isVerified && { isVerified: userFilters.isVerified }),
        ...(userFilters.authProvider && { authProvider: userFilters.authProvider }),
        ...(userFilters.gender && { gender: userFilters.gender }),
        ...(userFilters.profession && { profession: userFilters.profession }),
        sortBy: userFilters.sortBy,
        sortOrder: userFilters.sortOrder
      });

      const response = await fetch(
        `${BASE_URL}/api/v1/admin/all/users?${queryParams}`,
        {
          headers: {
            "authorization": `Bearer ${localStorage.getItem("token")}`
          }
        }
      );
      
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      
      if (data.success) {
        setUsers(data.users);
        setPagination(data.pagination);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, userFilters]);

  // Fetch Guests
  const fetchGuests = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...(guestFilters.search && { search: guestFilters.search }),
        ...(guestFilters.isBlacklisted && { isBlacklisted: guestFilters.isBlacklisted }),
        ...(guestFilters.hasOrders && { hasOrders: guestFilters.hasOrders }),
        sortBy: guestFilters.sortBy,
        sortOrder: guestFilters.sortOrder
      });

      const response = await fetch(
        `${BASE_URL}/api/v1/admin/all/guest-users?${queryParams}`,
        {
          headers: {
            "authorization": `Bearer ${localStorage.getItem("token")}`
          }
        }
      );
      
      if (!response.ok) throw new Error('Failed to fetch guests');
      const data = await response.json();
      
      if (data.success) {
        setGuests(data.guests);
        setPagination(data.pagination);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, guestFilters]);

  // Effect to fetch data when tab changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
    if (activeTab === 'users') {
      fetchUsers();
    } else {
      fetchGuests();
    }
  }, [activeTab]);

  // Effect to fetch data when filters change
  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else {
      fetchGuests();
    }
  }, [fetchUsers, fetchGuests]);

  // Handle search for users
  const handleUserSearch = useCallback((e) => {
    e.preventDefault();
    setUserFilters(prev => ({ ...prev, search: prev.searchInput }));
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  // Handle search for guests
  const handleGuestSearch = useCallback((e) => {
    e.preventDefault();
    setGuestFilters(prev => ({ ...prev, search: prev.searchInput }));
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  }, []);

  // Export Users/Guests to CSV
  const handleExport = useCallback(() => {
    const data = activeTab === 'users' ? users : guests;
    const headers = activeTab === 'users' 
      ? ['Name', 'Email', 'Phone', 'Role', 'Active', 'Verified', 'Created']
      : ['Name', 'Email', 'Phone', 'Orders', 'Total Spent', 'Blacklisted', 'Created'];
    
    const csvContent = [
      headers,
      ...data.map((item) => activeTab === 'users' 
        ? [
            `${item.firstName || ''} ${item.lastName || ''}`,
            item.email,
            item.phone || 'N/A',
            item.role,
            item.isActive ? 'Yes' : 'No',
            item.isVerified ? 'Yes' : 'No',
            new Date(item.createdAt).toLocaleDateString()
          ]
        : [
            item.fullName,
            item.email,
            item.phone,
            item.orderCount || 0,
            `₹${item.totalSpent || 0}`,
            item.isBlacklisted ? 'Yes' : 'No',
            new Date(item.createdAt).toLocaleDateString()
          ]
      )
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}-${Date.now()}.csv`;
    a.click();
  }, [activeTab, users, guests]);

  // Format date
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, []);

  const currentData = activeTab === 'users' ? users : guests;
  const currentFilters = activeTab === 'users' ? userFilters : guestFilters;
  const setCurrentFilters = activeTab === 'users' ? setUserFilters : setGuestFilters;

  return (
    <div className="flex w-full min-h-[calc(100dvh-60px)] flex-col gap-2 sm:gap-3 p-0 sm:p-0 md:p-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-3">
          <Users className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-[#F4A696]" />
          <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-[#1B3C5C]">
            User Management
          </h1>
        </div>
        <button
          onClick={handleExport}
          disabled={currentData.length === 0}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#F4A696] to-[#E89580] px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-white hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          <span className="hidden sm:inline">Export CSV</span>
          <span className="sm:hidden">Export</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg sm:rounded-xl border border-gray-200 shadow-sm">
        <div className="border-b border-gray-200">
          <div className="flex overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setActiveTab('users')}
              className={`flex-shrink-0 px-4 sm:px-6 py-3 sm:py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'users'
                  ? 'border-[#F4A696] text-[#F4A696]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>Registered Users</span>
                {users.length > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                    {pagination.total}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('guests')}
              className={`flex-shrink-0 px-4 sm:px-6 py-3 sm:py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'guests'
                  ? 'border-[#F4A696] text-[#F4A696]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                <span>Guest Users</span>
                {guests.length > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                    {pagination.total}
                  </span>
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-3 sm:p-4 md:p-6">
          {/* Search Bar */}
          <form
            onSubmit={activeTab === 'users' ? handleUserSearch : handleGuestSearch}
            className="flex gap-2 mb-4"
          >
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="text"
                placeholder={`Search ${activeTab === 'users' ? 'by name, email, phone...' : 'by name, email, phone...'}`}
                value={currentFilters.searchInput}
                onChange={(e) =>
                  setCurrentFilters(prev => ({ ...prev, searchInput: e.target.value }))
                }
                className="w-full pl-9 sm:pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F4A696] focus:border-transparent"
              />
            </div>
            <button
              type="submit"
              className="px-4 sm:px-6 py-2 bg-gradient-to-r from-[#F4A696] to-[#E89580] text-white rounded-lg hover:shadow-lg transition flex items-center gap-2 text-sm font-medium"
            >
              <Search className="w-4 h-4" />
              <span className="hidden sm:inline">Search</span>
            </button>
          </form>

          {/* Filters Section */}
          {activeTab === 'users' ? (
            <div className="flex flex-wrap gap-2 mb-4">
              <select
                value={userFilters.role}
                onChange={(e) => {
                  setUserFilters(prev => ({ ...prev, role: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="px-3 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F4A696] focus:border-transparent"
              >
                <option value="">All Roles</option>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>

              <select
                value={userFilters.isActive}
                onChange={(e) => {
                  setUserFilters(prev => ({ ...prev, isActive: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="px-3 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F4A696] focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>

              <select
                value={userFilters.isVerified}
                onChange={(e) => {
                  setUserFilters(prev => ({ ...prev, isVerified: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="px-3 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F4A696] focus:border-transparent"
              >
                <option value="">All Verification</option>
                <option value="true">Verified</option>
                <option value="false">Not Verified</option>
              </select>

              <select
                value={userFilters.authProvider}
                onChange={(e) => {
                  setUserFilters(prev => ({ ...prev, authProvider: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="px-3 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F4A696] focus:border-transparent"
              >
                <option value="">All Providers</option>
                <option value="local">Local</option>
                <option value="google">Google</option>
                <option value="facebook">Facebook</option>
              </select>

              <select
                value={userFilters.gender}
                onChange={(e) => {
                  setUserFilters(prev => ({ ...prev, gender: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="px-3 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F4A696] focus:border-transparent"
              >
                <option value="">All Genders</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>

              {/* Clear Filters */}
              <button
                onClick={() => {
                  setUserFilters({
                    search: '',
                    searchInput: '',
                    role: '',
                    isActive: '',
                    suspended: '',
                    isVerified: '',
                    authProvider: '',
                    gender: '',
                    profession: '',
                    sortBy: 'createdAt',
                    sortOrder: 'desc'
                  });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="px-3 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 mb-4">
              <select
                value={guestFilters.isBlacklisted}
                onChange={(e) => {
                  setGuestFilters(prev => ({ ...prev, isBlacklisted: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="px-3 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F4A696] focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="false">Active</option>
                <option value="true">Blacklisted</option>
              </select>

              <select
                value={guestFilters.hasOrders}
                onChange={(e) => {
                  setGuestFilters(prev => ({ ...prev, hasOrders: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="px-3 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F4A696] focus:border-transparent"
              >
                <option value="">All Guests</option>
                <option value="true">With Orders</option>
                <option value="false">No Orders</option>
              </select>

              {/* Clear Filters */}
              <button
                onClick={() => {
                  setGuestFilters({
                    search: '',
                    searchInput: '',
                    isBlacklisted: '',
                    hasOrders: '',
                    sortBy: 'createdAt',
                    sortOrder: 'desc'
                  });
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="px-3 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Results count */}
          <div className="mb-4 text-xs sm:text-sm text-gray-600 flex items-center gap-2">
            <Users className="w-4 h-4" />
            Showing {currentData.length} of {pagination.total} {activeTab}
          </div>

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
              <XCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-[#F4A696]" />
            </div>
          ) : currentData.length === 0 ? (
            /* No Results */
            <div className="flex flex-col items-center justify-center px-6 py-12">
              <Users className="h-12 w-12 text-gray-400 mb-3" />
              <p className="text-gray-500 font-medium">No {activeTab} found</p>
              <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filters</p>
            </div>
          ) : (
            /* Table */
            <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              {activeTab === 'users' ? (
                <table className="w-full min-w-[800px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Role & Provider
                      </th>
                      <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Joined
                      </th>
                      <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                          <div className="flex items-center gap-3">
                            {user.profilePicture ? (
                              <img
                                src={user.profilePicture}
                                alt={user.firstName}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFF4F0] to-[#FFE5DC] flex items-center justify-center">
                                <Users className="w-5 h-5 text-[#F4A696]" />
                              </div>
                            )}
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {user.firstName} {user.lastName}
                              </p>
                              {user.gender && (
                                <p className="text-xs text-gray-500 capitalize">{user.gender}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                          <div className="flex flex-col gap-1">
                            <p className="text-xs flex items-center gap-1 text-gray-600">
                              <Mail className="w-3 h-3" />
                              <span className="truncate max-w-[150px]">{user.email}</span>
                            </p>
                            {user.phone && (
                              <p className="text-xs flex items-center gap-1 text-gray-600">
                                <Phone className="w-3 h-3" />
                                {user.phone}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                          <div className="flex flex-col gap-1">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              user.role === 'admin' 
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {user.role === 'admin' ? <Shield className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                              {user.role}
                            </span>
                            <span className="text-xs text-gray-500 capitalize">
                              {user.authProvider}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                          <div className="flex flex-col gap-1">
                            {user.isActive ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                <CheckCircle2 className="w-3 h-3" />
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                <XCircle className="w-3 h-3" />
                                Inactive
                              </span>
                            )}
                            {user.isVerified && (
                              <span className="text-xs text-green-600 flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" />
                                Verified
                              </span>
                            )}
                            {user.suspended && (
                              <span className="text-xs text-red-600 flex items-center gap-1">
                                <Ban className="w-3 h-3" />
                                Suspended
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                          <div className="flex items-center gap-1 text-xs text-gray-600">
                            <Calendar className="w-3 h-3" />
                            {formatDate(user.createdAt)}
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                          <button
                            onClick={() => alert(`View user: ${user._id}`)}
                            className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-100 transition-colors"
                          >
                            <Eye className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                            <span className="hidden sm:inline">View</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full min-w-[800px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Guest
                      </th>
                      <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Orders & Spending
                      </th>
                      <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Last Booking
                      </th>
                      <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {guests.map((guest) => (
                      <tr key={guest._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFF4F0] to-[#FFE5DC] flex items-center justify-center">
                              <UserPlus className="w-5 h-5 text-[#F4A696]" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {guest.fullName}
                              </p>
                              <p className="text-xs text-gray-500">Guest User</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                          <div className="flex flex-col gap-1">
                            <p className="text-xs flex items-center gap-1 text-gray-600">
                              <Mail className="w-3 h-3" />
                              <span className="truncate max-w-[150px]">{guest.email}</span>
                            </p>
                            <p className="text-xs flex items-center gap-1 text-gray-600">
                              <Phone className="w-3 h-3" />
                              {guest.phone}
                            </p>
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                          <div className="flex flex-col gap-1">
                            <p className="text-xs flex items-center gap-1 text-gray-900">
                              <Package className="w-3 h-3" />
                              <span className="font-medium">{guest.orderCount || 0} orders</span>
                            </p>
                            <p className="text-xs flex items-center gap-1 text-green-600">
                              <DollarSign className="w-3 h-3" />
                              <span className="font-medium">₹{guest.totalSpent || 0}</span>
                            </p>
                          </div>
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                          {guest.isBlacklisted ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              <Ban className="w-3 h-3" />
                              Blacklisted
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <CheckCircle2 className="w-3 h-3" />
                              Active
                            </span>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                          {guest.lastBookingDate ? (
                            <div className="flex items-center gap-1 text-xs text-gray-600">
                              <Calendar className="w-3 h-3" />
                              {formatDate(guest.lastBookingDate)}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">No bookings</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 md:px-6 py-3 md:py-4">
                          <button
                            onClick={() => alert(`View guest: ${guest._id}`)}
                            className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-100 transition-colors"
                          >
                            <Eye className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                            <span className="hidden sm:inline">View</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs sm:text-sm text-gray-700 text-center sm:text-left">
                <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(pagination.page * pagination.limit, pagination.total)}
                </span>{' '}
                of <span className="font-medium">{pagination.total}</span>
              </p>
              <div className="flex items-center gap-1 sm:gap-2 justify-center">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrevPage}
                  className="flex-shrink-0 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Prev</span>
                </button>
                
                {[...Array(Math.min(pagination.totalPages, 3))].map((_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`flex-shrink-0 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                        pagination.page === pageNum
                          ? 'bg-[#F4A696] text-white'
                          : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNextPage}
                  className="flex-shrink-0 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scrollbar Styles */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-thin::-webkit-scrollbar {
          height: 8px;
          width: 8px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export  {UserManagement as Users};
